<template>
  <div class="search-picker">
    <div ref="searchContainer" class="search-picker__search-container"
         :class="{
             'search-picker__search-container--full-width': fullWidth,
             'search-picker__search-container--with-icon': withIcon
           }">
      <div v-if="withIcon" class="search-picker__search-icon"><i class="fa fa-search"></i></div>
      <input v-model="searchText"
             :id="inputId"
             ref="searchInput"
             :placeholder="placeholder"
             aria-owns="search-picker-list"
             :aria-expanded="showingList ? 'true' : 'false'"
             role="combobox"
             aria-autocomplete="list"
             autocomplete="off"
             class="search-picker__search"
             :class="{
               'search-picker__search--full-width': fullWidth,
               'search-picker__search--with-icon': withIcon
             }"
             @focus="showList"
             @keydown.down="descendHighlighted"
             @keydown.up="ascendHighlighted"
             @keydown.enter.prevent="selectHighlighted"
             @keydown.esc="hideList">
    </div>
    <div ref="listContainer"
         v-show="showingList"
         class="search-picker__options"
         :class="fullWidth ? 'w-full' : 'w-3/4'">
      <div v-if="fetching" class="search-picker__list spacer-y-3 margin-y-1">
        <span class="spacer-x-3">Loading...</span>
      </div>
      <ul v-else
          ref="list"
          id="search-picker-list"
          class="search-picker__list spacer-y-2 margin-y-1">
        <li v-for="(option, index) in options"
            :key="option.id"
            :ref="`option-${option.id}`"
            tabindex="-1"
            :aria-selected="isHighlighted(index) ? 'true' : 'false'"
            class="search-picker__list-item flex flex-col spacer-y-1"
            :class="{
              'search-picker__list-item--highlight': isHighlighted(index),
            }"
            @click="selectOption(option)">
          <div v-html="colourSearchTerm(option.name)"/>
          <slot name="list-item-content" v-bind:option="option"/>
        </li>
        <li class="search-picker__list-item spacer-y-1" v-if="customOption && canAdd">
          <button
            type="button"
            class="search-picker__button"
            @click="addCustom">
            <div>
              Add ‘<span class="search-picker__button--highlight">{{ customOption }}</span>’
            </div>
          </button>
        </li>
        <li class="search-picker__list-item spacer-y-1" v-else-if="customOption && !canAdd">
          No results, check for typos and search again
        </li>
      </ul>
      <div aria-live="polite" role="status" class="sr-only">
        {{ options.length }} results available.
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import cuid from 'cuid'
import { createPopper } from '@popperjs/core'

export default {
  data () {
    return {
      searchText: '',
      customOption: null,
      options: [],
      showingList: false,
      highlighted: -1,
      fetching: false
    }
  },

  props: {
    current: {
      type: Array,
      default: () => { return [] }
    },

    placeholder: {
      type: String,
      default: ''
    },

    fullWidth: {
      type: Boolean,
      default: false
    },

    withIcon: {
      type: Boolean,
      default: false
    },

    withPopper: {
      type: Boolean,
      default: true
    },

    parentNode: {
      type: Number,
      default: null
    },

    getData: {
      type: Function,
      required: true
    },

    canAdd: {
      type: Boolean,
      default: true
    },

    inputId: {
      type: String,
      default: null
    }
  },

  watch: {
    searchText () {
      if (!this.showingList) {
        this.showList()
      }

      this.textChange()
    },

    options () {
      if (this.$refs.list) {
        this.$refs.list.scrollTop = 0
      }
    },

    highlighted (value) {
      if (value >= 0) {
        const item = this.options[value]

        this.scrollIntoView(
          this.$refs[`option-${item.id}`][0],
          this.$refs.list
        )
      } else {
        if (this.$refs.list) {
          this.$refs.list.scrollTop = 0
        }
      }
    }
  },

  mounted () {
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    isHighlighted (index) {
      return index === this.highlighted
    },

    resetHighlighted () {
      this.highlighted = -1
    },

    descendHighlighted () {
      this.showList()
      if (this.highlighted + 1 < this.options.length) {
        this.highlighted = this.highlighted + 1
      }
    },

    ascendHighlighted () {
      if (this.highlighted > 0) {
        this.highlighted = this.highlighted - 1
      }
    },

    showList () {
      if (this.showingList) return

      this.showingList = true
      this.fetchOptions()

      if (this.fullWidth && this.withPopper) {
        createPopper(this.$refs.searchContainer, this.$refs.listContainer)
      }
    },

    hideList () {
      this.highlighted = -1
      this.showingList = false
      this.options = []
    },

    handleClickOutside (event) {
      if (
        this.showingList &&
        !this.$refs.searchInput.contains(event.target) &&
        !this.$refs.listContainer.contains(event.target)
      ) {
        this.hideList()
      }
    },

    colourSearchTerm (text) {
      return text.replace(
        new RegExp(this.searchText, 'gi'),
        match => {
          return (
            '<span class="text-primary">' + match + '</span>'
          )
        }
      )
    },

    scrollIntoView (ele, container) {
      const eleTop = ele.offsetTop
      const eleBottom = eleTop + ele.clientHeight

      const containerTop = container.scrollTop
      const containerBottom = containerTop + container.clientHeight

      if (eleBottom > containerBottom) {
        container.scrollTop = eleBottom - container.clientHeight
      } else if (eleTop < containerTop) {
        container.scrollTop = eleTop
      }
      if (this.highlighted === 0) {
        container.scrollTop = 0
      }
    },

    selectHighlighted () {
      if (!this.fetching && !this.options.length) {
        this.addCustom()
      } else if (this.highlighted >= 0) {
        this.selectOption(this.options[this.highlighted])
      }
    },

    selectOption (option) {
      this.hideList()
      this.searchText = ''
      this.$emit('selected', option)
    },

    addCustom () {
      this.$emit('selected', {
        id: cuid(),
        name: this.searchText,
        custom: true
      })
      this.hideList()
      this.searchText = ''
    },

    textChange: debounce(function () {
      this.fetchOptions()
    }, 300),

    async fetchOptions () {
      this.fetching = true
      const params = {
        search: this.searchText,
        selected: this.current
          .filter(element => !element.custom)
          .map(element => element.id),
        per_page: 15
      }
      const options = await this.getData(params)

      this.options = options
      this.fetching = false
      this.customOption =
        this.options.length === 0 && this.searchText
          ? this.searchText
          : null
      this.resetHighlighted()
    }
  }
}
</script>

<style lang="scss">
.search-picker {
  position: relative;

  &__search-container {
    position: relative;
    &--full-width {
      background: white;
      width: 100%;
      border: black 2px solid;
    }
  }

  &__search-icon {
    position: absolute;
    top: 0;
    left: 0;
    padding: 15px;
    color: #757575;
  }

  &__search {
    font-size: 16px;
    &:focus {
      outline: none;
    }
    &--full-width {
      padding: 10px;
      border: none;
      width: 100%;
    }
    &--with-icon {
      padding: 15px 15px 15px 40px;
    }
    &::placeholder {
      color: #9b9b9b;
      padding-left: 2px;
    }
  }

  &__options {
    position: absolute;
    min-width: 300px;
    z-index: 80;
  }

  &__list {
    list-style-type: none;
    padding-left: 0;
    background-color: white;
    width: 100%;
    border: black solid 2px;
    max-height: 24rem;
    overflow: scroll;
    color: black;
    font-size: 14px;
    font-weight: 700;
  }
  &__list-item {
    margin-right: 20px;
    margin-left: 20px;
    color: #404040;
    cursor: pointer;
    min-height: 40px;
    &:hover {
      color: black;
    }
    &:not(:first-child) {
      border-top: #E0E0E0 1px solid;
    }
  }

  &__list-item--highlight {
    color: black;
  }

  &__subtext {
    color: #71717a;
    font-size: 11px;
    font-weight: 500;
  }

  &__button {
    background-color: white;
    border: none;
    div {
      color: #53535b;
    }
    &--highlight {
      &:hover {
        color: var(--brand-primary);
      }
    }
  }
}
</style>
